import { FC, useEffect, useState } from 'react'
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import { checklistReservaService, mensagemErroChecklistReserva } from '@/services/checklistReservaService'
import { CODIGOS_MOTIVO_RECUSA, ROTULOS_CODIGO_MOTIVO } from '@/constants/statusChecklist'
import type { CodigoMotivoRecusa, DevolverItemResponse } from '@/types/checklist'
import type { ItemChecklistReserva } from '@/types/estruturacao'
import { rotuloDoItem, statusDoItem } from '@/utils/checklistReserva'

const MAX_COMENTARIO = 500

interface Props {
  open: boolean
  reservaId: string
  item: ItemChecklistReserva | null
  onFechar: () => void
  onDevolvido: (resposta: DevolverItemResponse) => void
}

/**
 * Devolver um item ao consultor como pendência (Jornada ADM 30/10, item 3) — back-office, com a
 * reserva na curadoria. Recusa o arquivo (se houver) e põe a reserva em pendência; o consultor é
 * avisado. Comentário opcional: sem ele, a pendência pede o documento pelo nome.
 */
export const DevolverItemDialog: FC<Props> = ({ open, reservaId, item, onFechar, onDevolvido }) => {
  const [comentario, setComentario] = useState('')
  const [codigo, setCodigo] = useState<CodigoMotivoRecusa | ''>('')
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setComentario('')
      setCodigo('')
      setErro(null)
    }
  }, [open, item])

  if (!item) return null
  const rotulo = rotuloDoItem(item)
  const temArquivo = statusDoItem(item) !== 'FALTANDO'
  const podeSalvar = !salvando && comentario.length <= MAX_COMENTARIO

  const salvar = async () => {
    if (!item.chave || !podeSalvar) return
    setSalvando(true)
    setErro(null)
    try {
      onDevolvido(
        await checklistReservaService.devolver(reservaId, item.chave, {
          comentario: comentario.trim() || null,
          codigoMotivo: temArquivo ? codigo || null : null,
        })
      )
    } catch (e) {
      setErro(mensagemErroChecklistReserva(e, 'Não foi possível devolver o documento.'))
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Dialog open={open} onClose={salvando ? undefined : onFechar} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Devolver ao consultor: {rotulo}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {temArquivo ? 'O arquivo atual será recusado e a ' : 'A '}
          reserva vai para pendência. O consultor recebe o aviso e anexa o documento correto pela
          Central de Pendências.
        </Typography>
        {temArquivo && (
          <TextField
            select
            label="Código do motivo (opcional)"
            value={codigo}
            onChange={(e) => setCodigo(e.target.value as CodigoMotivoRecusa | '')}
            fullWidth
            disabled={salvando}
            sx={{ mb: 2 }}
          >
            <MenuItem value="">Sem código</MenuItem>
            {CODIGOS_MOTIVO_RECUSA.map((c) => (
              <MenuItem key={c} value={c}>
                {ROTULOS_CODIGO_MOTIVO[c]}
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          label="Comentário para o consultor (opcional)"
          value={comentario}
          onChange={(e) => setComentario(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          disabled={salvando}
          error={comentario.length > MAX_COMENTARIO}
          helperText={`${comentario.length}/${MAX_COMENTARIO}`}
        />
        {erro && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {erro}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onFechar} disabled={salvando}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={() => void salvar()}
          disabled={!podeSalvar}
          startIcon={salvando ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          Devolver como pendência
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DevolverItemDialog
